import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import Pusher, { Channel } from "pusher-js";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { registerPushNotifications, useSessionUser } from "@web-app/api";
import config from "@web-app/config";

import { ChatMessage, ConversationWithDetails } from "../../api/services/chat";

import IncomingCallModal, { IncomingCallData } from "./IncomingCallModal";

const originalTitle = document.title;
let blinkInterval: ReturnType<typeof setInterval> | null = null;

function startTitleBlink(notificationTitle: string) {
  stopTitleBlink();
  let showNotification = true;
  blinkInterval = setInterval(() => {
    document.title = showNotification ? notificationTitle : originalTitle;
    showNotification = !showNotification;
  }, 1000);
  document.title = notificationTitle;
}

function stopTitleBlink() {
  if (blinkInterval) {
    clearInterval(blinkInterval);
    blinkInterval = null;
  }
  document.title = originalTitle;
}

interface ChatEventsProviderProps {
  children: ReactNode;
}

export default function ChatEventsProvider({
  children,
}: ChatEventsProviderProps) {
  const [incomingCall, setIncomingCall] = useState<IncomingCallData | null>(
    null,
  );
  const { data: currentUser } = useSessionUser();
  const queryClient = useQueryClient();
  const pusherRef = useRef<Pusher | null>(null);
  const channelRef = useRef<Channel | null>(null);

  const handleCallInitiated = useCallback(
    (data: IncomingCallData) => {
      if (data.initiatorId === currentUser?.id) {
        return;
      }

      if (!data.participantIds?.includes(currentUser?.id ?? "")) {
        return;
      }

      setIncomingCall(data);

      const orderId = data.orderExternalId || data.orderDisplayId || "";
      const notificationTitle = orderId
        ? `Order ${orderId} - Incoming Call`
        : "Incoming Call";
      startTitleBlink(notificationTitle);
    },
    [currentUser?.id],
  );

  const handleMessageReceived = useCallback(
    (data: {
      action: string;
      payload: ChatMessage;
      sender?: { id: string; image: string; name: string };
    }) => {
      if (data.action === "messageReceived" && data.payload) {
        const message: ChatMessage = data.payload;
        const senderInfo = data.sender;
        const isFromOtherUser = message.senderId !== currentUser?.id;

        const conversations = queryClient.getQueryData<
          InfiniteData<ConversationWithDetails[]>
        >(["chat", "conversations"]);
        const conversation = conversations?.pages
          .flat()
          .find((c) => c.conversation.id === message.conversationId);

        if (!conversation) return;

        if (isFromOtherUser) {
          const orderId =
            conversation?.orderExternalId || conversation?.orderDisplayId || "";
          const notificationType =
            message.messageType === "CALL" ? "Incoming Call" : "New Message";
          const notificationTitle = orderId
            ? `Order ${orderId} - ${notificationType}`
            : notificationType;
          startTitleBlink(notificationTitle);
        }

        queryClient.setQueryData(
          ["chat", "conversations"],
          (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
            if (!old) return old;

            const updatedPages = old.pages.map((page) =>
              page.map((conv: ConversationWithDetails) => {
                if (conv.conversation.id === message.conversationId) {
                  let updatedParticipants = conv.participantsWithDetails.map(
                    (p) => {
                      if (p.userId === message.senderId) {
                        return { ...p, hasReadLast: true };
                      }
                      return { ...p, hasReadLast: false };
                    },
                  );

                  const senderExists = updatedParticipants.some(
                    (p) => p.userId === message.senderId,
                  );
                  if (!senderExists && senderInfo) {
                    updatedParticipants = [
                      ...updatedParticipants,
                      {
                        hasReadLast: true,
                        image: senderInfo.image,
                        name: senderInfo.name,
                        phone: "",
                        role: "STAFF",
                        userId: senderInfo.id,
                      },
                    ];
                  }

                  return {
                    ...conv,
                    conversation: {
                      ...conv.conversation,
                      lastMessageAt: message.createdAt,
                    },
                    lastMessage: message,
                    participantsWithDetails: updatedParticipants,
                    unreadCount: isFromOtherUser
                      ? conv.unreadCount + 1
                      : conv.unreadCount,
                  };
                }
                return conv;
              }),
            );

            const allConversations = updatedPages.flat();
            allConversations.sort(
              (a: ConversationWithDetails, b: ConversationWithDetails) =>
                new Date(b.conversation.lastMessageAt).getTime() -
                new Date(a.conversation.lastMessageAt).getTime(),
            );

            const pageSize = old.pages[0]?.length || 10;
            const newPages: ConversationWithDetails[][] = [];
            for (let i = 0; i < allConversations.length; i += pageSize) {
              newPages.push(allConversations.slice(i, i + pageSize));
            }
            if (newPages.length === 0) newPages.push([]);

            return { ...old, pages: newPages };
          },
        );

        const isCallMessage = message.messageType === "CALL";
        const shouldAddToCache = isFromOtherUser || isCallMessage;

        if (shouldAddToCache) {
          queryClient.setQueryData(
            ["chat", "messages", message.conversationId],
            (old: InfiniteData<ChatMessage[]> | undefined) => {
              if (!old) return { pageParams: [0], pages: [[message]] };

              let messageExists = false;
              const updatedPages = old.pages.map((page) =>
                page.map((m: ChatMessage) => {
                  if (m.id === message.id) {
                    messageExists = true;
                    return message;
                  }
                  return m;
                }),
              );

              if (messageExists) {
                return { ...old, pages: updatedPages };
              }

              const lastPageIndex = old.pages.length - 1;
              return {
                ...old,
                pages: [
                  ...old.pages.slice(0, lastPageIndex),
                  [...old.pages[lastPageIndex], message],
                ],
              };
            },
          );
        }
      }
    },
    [currentUser?.id, queryClient],
  );

  const handleMessageRead = useCallback(
    (data: {
      action: string;
      payload: { conversationId: string; readBy: string };
    }) => {
      if (data.action === "messagesRead" && data.payload) {
        const { conversationId: readConversationId, readBy } = data.payload;

        queryClient.setQueryData(
          ["chat", "conversations"],
          (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
            if (!old) return old;

            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((conv: ConversationWithDetails) => {
                  if (conv.conversation.id === readConversationId) {
                    const updatedParticipants =
                      conv.participantsWithDetails.map((p) => {
                        if (p.userId === readBy) {
                          return { ...p, hasReadLast: true };
                        }
                        return p;
                      });

                    if (
                      readBy !== currentUser?.id &&
                      conv.lastMessage?.senderId === currentUser?.id
                    ) {
                      return {
                        ...conv,
                        lastMessage: conv.lastMessage
                          ? {
                              ...conv.lastMessage,
                              isRead: true,
                              readAt: new Date().toISOString(),
                            }
                          : null,
                        participantsWithDetails: updatedParticipants,
                      };
                    }

                    if (readBy === currentUser?.id) {
                      return {
                        ...conv,
                        participantsWithDetails: updatedParticipants,
                        unreadCount: 0,
                      };
                    }

                    return {
                      ...conv,
                      participantsWithDetails: updatedParticipants,
                    };
                  }
                  return conv;
                }),
              ),
            };
          },
        );

        if (readBy !== currentUser?.id) {
          queryClient.setQueryData(
            ["chat", "messages", readConversationId],
            (old: InfiniteData<ChatMessage[]> | undefined) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) =>
                  page.map((msg: ChatMessage) => ({
                    ...msg,
                    isRead:
                      msg.senderId === currentUser?.id ? true : msg.isRead,
                    readAt:
                      msg.senderId === currentUser?.id
                        ? new Date().toISOString()
                        : msg.readAt,
                  })),
                ),
              };
            },
          );
        }
      }
    },
    [currentUser?.id, queryClient],
  );

  const handleConversationCreated = useCallback(
    (data: { action: string; payload: ConversationWithDetails }) => {
      if (data.action === "conversationCreated" && data.payload) {
        const newConversation = data.payload;

        const isParticipant = newConversation.participantsWithDetails.some(
          (p) => p.userId === currentUser?.id,
        );

        if (!isParticipant) return;

        queryClient.setQueryData(
          ["chat", "conversations"],
          (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
            if (!old) return { pageParams: [0], pages: [[newConversation]] };

            const firstPage = old.pages[0];
            const exists = firstPage.find(
              (c) => c.conversation.id === newConversation.conversation.id,
            );
            if (exists) return old;

            return {
              ...old,
              pages: [[newConversation, ...firstPage], ...old.pages.slice(1)],
            };
          },
        );
      }
    },
    [currentUser?.id, queryClient],
  );

  const handleConversationUpdated = useCallback(
    (data: { action: string; payload: ConversationWithDetails }) => {
      if (data.action === "conversationUpdated" && data.payload) {
        const updatedConversation = data.payload;

        queryClient.setQueryData(
          ["chat", "conversations"],
          (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
            if (!old) return old;

            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((conv: ConversationWithDetails) => {
                  if (
                    conv.conversation.id === updatedConversation.conversation.id
                  ) {
                    return {
                      ...conv,
                      conversation: {
                        ...conv.conversation,
                        ...updatedConversation.conversation,
                      },
                      lastMessage: updatedConversation.lastMessage,
                      participantsWithDetails:
                        updatedConversation.participantsWithDetails,
                    };
                  }
                  return conv;
                }),
              ),
            };
          },
        );
      }
    },
    [queryClient],
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        stopTitleBlink();
      }
    };

    const handleFocus = () => {
      stopTitleBlink();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      stopTitleBlink();
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    if (pusherRef.current) return;

    const pusher = new Pusher(config.SOKETI_APP_KEY, {
      cluster: "",
      enabledTransports: ["ws", "wss"],
      forceTLS: false,
      wsHost: config.SOKETI_HOST,
      wsPort: parseInt(config.SOKETI_PORT),
      wssPort: parseInt(config.SOKETI_PORT),
    });

    pusher.connection.bind("error", () => {
      console.error("Soketi (Chat) - Something went wrong!");
    });

    pusher.connection.bind("connected", () => {
      console.info("Soketi (Chat) - Connected!");
    });

    pusherRef.current = pusher;

    const workspaceChannelName = `internal-api@${currentUser.currentWorkspace.id}`;
    const workspaceChannel = pusher.subscribe(workspaceChannelName);
    channelRef.current = workspaceChannel;

    workspaceChannel.bind("callInitiated", handleCallInitiated);
    workspaceChannel.bind("chatMessageReceived", handleMessageReceived);
    workspaceChannel.bind("chatMessageRead", handleMessageRead);
    workspaceChannel.bind("conversationCreated", handleConversationCreated);
    workspaceChannel.bind("conversationUpdated", handleConversationUpdated);

    return () => {
      if (channelRef.current) {
        channelRef.current.unbind("callInitiated", handleCallInitiated);
        channelRef.current.unbind("chatMessageReceived", handleMessageReceived);
        channelRef.current.unbind("chatMessageRead", handleMessageRead);
        channelRef.current.unbind(
          "conversationCreated",
          handleConversationCreated,
        );
        channelRef.current.unbind(
          "conversationUpdated",
          handleConversationUpdated,
        );
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, [
    currentUser,
    handleCallInitiated,
    handleConversationCreated,
    handleConversationUpdated,
    handleMessageRead,
    handleMessageReceived,
  ]);

  useEffect(() => {
    if (!currentUser) return;
    registerPushNotifications();
  }, [currentUser]);

  const handleAccept = () => {
    if (!incomingCall) return;

    const popupWidth = 400;
    const popupHeight = 600;
    const left = window.screenX + (window.outerWidth - popupWidth) / 2;
    const top = window.screenY + (window.outerHeight - popupHeight) / 2;

    window.open(
      `/call/${incomingCall.conversationId}`,
      "voice-call",
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes`,
    );

    setIncomingCall(null);
  };

  const handleDecline = () => {
    setIncomingCall(null);
  };

  return (
    <>
      {children}
      {incomingCall && (
        <IncomingCallModal
          callData={incomingCall}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      )}
    </>
  );
}
